// Demo data for the landing trip-builder wizard. Migrated from the retired
// standalone demos (FeatureSections meal planner, GearManagementAnimation,
// SharingPermissionsDemo) so the wizard stays consistent with the product.

// ─── Crew ────────────────────────────────────────────────────────────────────

export type CrewId = 'alex' | 'priya' | 'sam' | 'jordan' | 'riley'

export interface WizardCrewMember {
  id: CrewId
  name: string
  shortName: string
  initials: string
  rig: string
  tags: string[]
}

export const WIZARD_CREW: WizardCrewMember[] = [
  { id: 'alex',   name: 'Alex',   shortName: 'Alex M.',   initials: 'AL', rig: '4Runner TRD',   tags: [] },
  { id: 'priya',  name: 'Priya',  shortName: 'Priya R.',  initials: 'PR', rig: 'Tacoma + RTT',  tags: ['vegetarian'] },
  { id: 'sam',    name: 'Sam',    shortName: 'Sam K.',    initials: 'SM', rig: 'Defender 110',  tags: ['nut-allergy'] },
  { id: 'jordan', name: 'Jordan', shortName: 'Jordan T.', initials: 'JO', rig: 'Gladiator',     tags: [] },
  { id: 'riley',  name: 'Riley',  shortName: 'Riley C.',  initials: 'RC', rig: 'Bronco Badlands', tags: [] },
]

export const DEFAULT_CREW_SELECTION: Record<CrewId, boolean> = {
  alex: true, priya: true, sam: true, jordan: true, riley: false,
}

export const crewById = (id: CrewId): WizardCrewMember =>
  WIZARD_CREW.find((m) => m.id === id) as WizardCrewMember

// ─── Meals ───────────────────────────────────────────────────────────────────

export type MealId = 'chili' | 'tacos' | 'cobbler' | 'breakfast'

export interface WizardMeal {
  id: MealId
  name: string
  slot: string
  vegetarian: boolean
  nuts: boolean
}

export const WIZARD_MEALS: WizardMeal[] = [
  { id: 'chili',     name: 'Campfire Chili',      slot: 'Fri · Dinner',  vegetarian: true,  nuts: false },
  { id: 'tacos',     name: 'Trailhead Tacos',     slot: 'Sat · Lunch',   vegetarian: false, nuts: false },
  { id: 'cobbler',   name: 'Dutch Oven Cobbler',  slot: 'Sat · Dessert', vegetarian: true,  nuts: true },
  { id: 'breakfast', name: 'Cast Iron Breakfast', slot: 'Sun · Morning', vegetarian: false, nuts: false },
]

export const DEFAULT_COOKS: Record<MealId, CrewId> = {
  chili: 'alex', tacos: 'priya', cobbler: 'sam', breakfast: 'jordan',
}

export function getMealConflict(meal: WizardMeal, cookId: CrewId): string | null {
  const cook = crewById(cookId)
  if (cook.tags.includes('vegetarian') && !meal.vegetarian) return `${cook.name} is vegetarian`
  if (cook.tags.includes('nut-allergy') && meal.nuts) return `${cook.name} has a nut allergy`
  return null
}

// ─── Gear ────────────────────────────────────────────────────────────────────

export type GearKey = 'recovery' | 'water' | 'kitchen' | 'comms' | 'solar' | 'firstaid'

export interface WizardGearItem {
  key: GearKey
  label: string
  owner: CrewId
}

export const WIZARD_GEAR: WizardGearItem[] = [
  { key: 'recovery', label: 'Recovery Kit',  owner: 'alex' },
  { key: 'water',    label: 'Water System',  owner: 'jordan' },
  { key: 'kitchen',  label: 'Camp Kitchen',  owner: 'sam' },
  { key: 'comms',    label: 'CB Radio',      owner: 'jordan' },
  { key: 'solar',    label: 'Solar Panels',  owner: 'alex' },
  { key: 'firstaid', label: 'First Aid Kit', owner: 'sam' },
]

export const DEFAULT_GEAR_PACKED: Record<GearKey, boolean> = {
  recovery: true, water: false, kitchen: true, comms: false, solar: false, firstaid: true,
}

// ─── Route stops ─────────────────────────────────────────────────────────────

export type StopId = 'barstow' | 'hiddenValley' | 'amboy' | 'twentynine'

export interface RouteStop {
  id: StopId
  name: string
  kind: 'start' | 'camp' | 'scenic' | 'end'
  locked: boolean
  /** Extra miles this stop adds to the base route when enabled */
  miles: number
  /** Campspotter-style camp score shown while the stop is enabled */
  camp?: { detail: string; score: number; rating: string; tone: 'good' | 'ok' | 'warn' }
  /** Real-world position rendered on the wizard's Mapbox map */
  lngLat: [number, number]
}

/** Barstow → Twentynine Palms with every optional stop off */
export const ROUTE_BASE_MILES = 152

export const ROUTE_STOPS: RouteStop[] = [
  { id: 'barstow', name: 'Barstow Staging', kind: 'start', locked: true, miles: 0, lngLat: [-117.0173, 34.8958] },
  {
    id: 'hiddenValley', name: 'Hidden Valley Camp', kind: 'camp', locked: false, miles: 47,
    camp: { detail: '41°F low · 6 mph', score: 9.1, rating: 'Great', tone: 'good' }, lngLat: [-116.1050, 34.9410] },
  {
    id: 'amboy', name: 'Amboy Crater Overlook', kind: 'scenic', locked: false, miles: 48,
    camp: { detail: '38°F low · 14 mph wind', score: 7.4, rating: 'Good', tone: 'ok' }, lngLat: [-115.7887, 34.5410] },
  { id: 'twentynine', name: 'Twentynine Palms', kind: 'end', locked: true, miles: 0, lngLat: [-116.0542, 34.1355] },
]

export const DEFAULT_STOPS: Record<StopId, boolean> = {
  barstow: true, hiddenValley: true, amboy: false, twentynine: true,
}

// ─── Sharing permissions ─────────────────────────────────────────────────────

export type SharingPermKey = 'gps' | 'campsites' | 'route' | 'crew' | 'notes'

export const SHARING_PERMS: { key: SharingPermKey; label: string }[] = [
  { key: 'gps',       label: 'GPS coordinates' },
  { key: 'campsites', label: 'Campsite coordinates' },
  { key: 'route',     label: 'Route geometry' },
  { key: 'crew',      label: 'Crew list' },
  { key: 'notes',     label: 'Notes & details' },
]

export const DEFAULT_PERMS: Record<SharingPermKey, boolean> = {
  gps: false, campsites: false, route: true, crew: false, notes: true,
}

export interface GuestField {
  key: SharingPermKey
  label: string
  value: string
  hiddenValue: string
  visible: boolean
}

/** The "Guest sees" preview fields for a given permission set. `totalMiles` and
 * `crewNames` let the preview reflect live wizard state. */
export function visibleFields(
  perms: Record<SharingPermKey, boolean>,
  opts?: { totalMiles?: number; crewNames?: string[] },
): GuestField[] {
  const miles = opts?.totalMiles ?? 247
  const crewNames = opts?.crewNames ?? ['Alex M.', 'Priya R.', 'Sam K.', 'Jordan T.']
  const crewValue =
    crewNames.length > 3
      ? `${crewNames.slice(0, 3).join(', ')}, +${crewNames.length - 3}`
      : crewNames.join(', ')
  return [
    { key: 'gps',       label: 'Start location',        value: '34.052°N 116.220°W · Barstow',       hiddenValue: 'Location hidden',                        visible: perms.gps },
    { key: 'campsites', label: 'Campsite coordinates',  value: '34.127°N 115.844°W · Hidden Valley', hiddenValue: 'Campsite pins hidden',                   visible: perms.campsites },
    { key: 'route',     label: 'Route',                 value: `${miles} mi · Mojave → Twentynine Palms`, hiddenValue: 'Route hidden',                      visible: perms.route },
    { key: 'crew',      label: 'Crew',                  value: crewValue,                            hiddenValue: `${crewNames.length} members · names hidden`, visible: perms.crew },
    { key: 'notes',     label: 'Notes',                 value: 'CB ch. 19 · Sand ladders req.',      hiddenValue: 'Notes hidden',                           visible: perms.notes },
  ]
}
